import React from 'react'
import styled from 'styled-components'
import Header from '../common/header'
import Footer from '../common/footer'
import Spacer from '../lib/spacer'
import { getPublishedPosts } from './posts'
import { COMPARE_PAGES } from '../compare/pages'
import { DEFAULT_LANG } from '../lib/locale'
import { useTranslation, useLang, LocalizedLink } from '../lib/i18n'
import { formatDate } from './format_date'
import VersionBanner from './version-banner'
import { small } from '../lib/media_query'
import { FaArrowRight } from 'react-icons/fa'

const PageWrapper = styled.div``

const PageHeader = styled.div`
    padding: 60px 0 40px;
    margin-bottom: 8px;

    h1 {
        font-size: 42px;
        font-weight: 700;
        color: ${(props) => props.theme.palette.greyScale.darkGrey};
        margin-bottom: 8px;
    }

    p {
        font-size: 16px;
        color: ${(props) => props.theme.palette.greyScale.grey};
        margin: 0;
    }

    ${small(`
        padding: 40px 0 28px;
        h1 { font-size: 32px; }
    `)}
`

const PostGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 36px;

    ${small(`
        grid-template-columns: 1fr;
        gap: 28px;
    `)}

    @media screen and (min-width: 610px) and (max-width: 949px) {
        grid-template-columns: repeat(2, 1fr);
    }
`

const PostCard = styled(LocalizedLink)`
    display: block;
    text-decoration: none;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.palette.greyScale.lighterGrey};
    transition: box-shadow 0.2s ease, transform 0.2s ease;

    &:hover {
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }
`

const PostImage = styled.div`
    width: 100%;
    aspect-ratio: 1400 / 703;
    overflow: hidden;
    background: #06091c;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: top;
        display: block;
    }
`

const PostBody = styled.div`
    padding: 20px 22px 24px;
`

const PostDate = styled.span`
    font-size: 12px;
    color: ${(props) => props.theme.palette.greyScale.lightGrey};
    letter-spacing: 1px;
    text-transform: uppercase;
    display: block;
    margin-bottom: 8px;
`

const PostTitle = styled.h2`
    font-size: 18px;
    font-weight: 700;
    color: ${(props) => props.theme.palette.greyScale.darkGrey};
    margin-bottom: 10px;
    line-height: 1.35;
`

const Comparisons = styled.section`
    margin-top: 64px;
    padding-top: 32px;
    border-top: 1px solid ${(props) => props.theme.palette.greyScale.lighterGrey};

    h2 {
        font-size: 12px;
        font-weight: 600;
        color: ${(props) => props.theme.palette.greyScale.lightGrey};
        letter-spacing: 1px;
        text-transform: uppercase;
        margin-bottom: 16px;
    }
`

const ComparisonGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 4px 36px;

    ${small(`
        grid-template-columns: 1fr;
    `)}

    @media screen and (min-width: 610px) and (max-width: 949px) {
        grid-template-columns: repeat(2, 1fr);
    }
`

const ComparisonLink = styled(LocalizedLink)`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 0;
    font-size: 14px;
    line-height: 1.4;
    color: ${(props) => props.theme.palette.greyScale.grey};
    text-decoration: none;
    border-bottom: 1px solid ${(props) => props.theme.palette.greyScale.lightestGrey};
    transition: color 0.2s ease;

    svg {
        flex-shrink: 0;
        font-size: 11px;
        color: ${(props) => props.theme.palette.greyScale.lightGrey};
        transition: transform 0.2s ease, color 0.2s ease;
    }

    &:hover {
        color: ${(props) => props.theme.palette.brand.blue};

        svg {
            color: ${(props) => props.theme.palette.brand.blue};
            transform: translateX(3px);
        }
    }
`

const PostExcerpt = styled.p`
    font-size: 14px;
    color: ${(props) => props.theme.palette.greyScale.grey};
    line-height: 1.6;
    margin: 0;
`

export default function BlogIndex() {
    const { t } = useTranslation()
    const lang = useLang()
    const posts = getPublishedPosts(lang)
    const dateLocale = t('dateLocale')

    return (
        <PageWrapper>
            <Header />
            <div className="grid-container">
                <PageHeader>
                    <h1>{t('blog.heading')}</h1>
                    <p>{t('blog.subheading')}</p>
                </PageHeader>
                <PostGrid>
                    {posts.map((post) => (
                        <PostCard to={`/blog/${post.slug}`} key={post.slug}>
                            <PostImage>
                                {post.image
                                    ? <img src={post.image} alt={post.title} />
                                    : <VersionBanner version={post.version} />
                                }
                            </PostImage>
                            <PostBody>
                                <PostDate>{formatDate(post.date, dateLocale)}</PostDate>
                                <PostTitle>{post.title}</PostTitle>
                                <PostExcerpt>{post.excerpt}</PostExcerpt>
                            </PostBody>
                        </PostCard>
                    ))}
                </PostGrid>
                {lang === DEFAULT_LANG && (
                    <Comparisons>
                        <h2>Break reminder apps compared</h2>
                        <ComparisonGrid>
                            {COMPARE_PAGES.map(({ slug, h1 }) => (
                                <ComparisonLink to={`/blog/${slug}`} key={slug}>
                                    {h1}
                                    <FaArrowRight />
                                </ComparisonLink>
                            ))}
                        </ComparisonGrid>
                    </Comparisons>
                )}
                <Spacer size={80} />
            </div>
            <Footer />
        </PageWrapper>
    )
}
